import { useMemo } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useQuery } from "@apollo/client";
import Button from "@/components/Button";
import Divider from "@/components/Divider";
import ProductFields from "../components/ProductFields";
import SeminarFeeFields from "../components/SeminarFeeFields";
import PaymentSummary from "../components/PaymentSummary";
import { useStepVisibility } from "../FormVisibilityContext";
import { GET_PRODUCT_VARIANTS } from "@/pages/events/queries";
import type { FormValues } from "../validation/schema";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import { isSeminarFormEvent } from "../utils";

type ProductVariantOptionNode = {
  id: string;
  name: string;
  priceCents: string;
};

const EMPTY_PRODUCT = {
  id: "",
  name: "",
  variantId: "",
  variantName: "",
  variantPrice: 0,
  collection: ""
};

const EMPTY_EVENT_FEE = {
  kind: "catalog" as const,
  paymentCollection: "registration" as const,
  productVariantId: "",
  productVariantName: "",
  amountCents: 0,
  displayName: ""
};

function LegacyPricingFields({ hasRegistrations }: { hasRegistrations: boolean }) {
  const { control, watch } = useFormContext<FormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "products"
  });

  const hasSelectedProduct = !!watch("products.0.id");
  const hasMultipleProducts = fields.length > 1;

  const addProduct = () => {
    append(EMPTY_PRODUCT);
  };

  return (
    <>
      <p className="text-body-medium">
        Choose existing product(s) or leave blank if the event is free. Payment can be collected during registration or
        at the event location if applicable. All registered sessions are included in the price. Note: Changes can be
        made until the first registration has been submitted.
      </p>
      <div className="flex flex-col gap-6">
        {fields.map((field, index) => (
          <ProductFields
            key={field.id}
            index={index}
            hasRegistrations={hasRegistrations}
            hasMultipleProducts={hasMultipleProducts}
            onRemove={() => {
              if (!hasRegistrations && hasMultipleProducts) {
                remove(index);
              }
            }}
          />
        ))}
      </div>
      {hasSelectedProduct && (
        <>
          <div>
            <Button variant="filledTonal" icon="add" theme="info" onClick={addProduct} disabled={hasRegistrations}>
              Add Product
            </Button>
          </div>
          <div className="py-3">
            <Divider />
          </div>
          <div>
            <PaymentSummary />
          </div>
        </>
      )}
    </>
  );
}

function SeminarPricingFields({ hasRegistrations, disabled }: { hasRegistrations: boolean; disabled: boolean }) {
  const { control, watch } = useFormContext<FormValues>();
  const orderTemplateId = watch("orderTemplateId");
  const { fields, append, remove } = useFieldArray({
    control,
    name: "eventFees"
  });

  const { data, loading } = useQuery(GET_PRODUCT_VARIANTS, {
    variables: { templateId: orderTemplateId ?? "" },
    skip: !orderTemplateId
  });

  const variantOptions = useMemo(
    () =>
      data?.commerceProductVariants.nodes.map((variant: ProductVariantOptionNode) => ({
        id: variant.id,
        value: variant.id,
        displayText: `${variant.name} (${centsToDollarsFormatted(parseInt(variant.priceCents, 10))})`,
        name: variant.name,
        priceCents: variant.priceCents
      })) ?? [],
    [data?.commerceProductVariants.nodes]
  );

  const hasMultipleFees = fields.length > 1;

  return (
    <>
      <p className="text-body-medium">
        Displayed fees can be collected by the PGA or by a third party. If collected by the PGA, fees must be collected
        at registration. Fees collected by third parties are shown for informative purposes only and must be collected
        indpendently.
      </p>
      <div className="flex flex-col gap-6">
        {fields.map((field, index) => (
          <SeminarFeeFields
            key={field.id}
            index={index}
            hasRegistrations={hasRegistrations}
            hasMultipleFees={hasMultipleFees}
            hasTemplateVariants={!!orderTemplateId}
            variantOptions={variantOptions}
            variantsLoading={loading}
            showRemoveButton={!disabled}
            onRemove={() => {
              if (!hasRegistrations && hasMultipleFees) {
                remove(index);
              }
            }}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-3">
        <Button
          variant="filledTonal"
          icon="add"
          theme="info"
          onClick={() => append(EMPTY_EVENT_FEE)}
          disabled={hasRegistrations || disabled}
        >
          Add Fee
        </Button>
      </div>
      {fields.length > 0 && (
        <>
          <div className="py-3">
            <Divider />
          </div>
          <div>
            <PaymentSummary />
          </div>
        </>
      )}
    </>
  );
}

const PricingStep = ({ hasRegistrations }: { hasRegistrations: boolean }) => {
  const { disabled } = useStepVisibility();
  const { watch } = useFormContext<FormValues>();
  const eventTypeName = watch("eventType.name");
  const orderTemplateId = watch("orderTemplateId");
  const eventFees = watch("eventFees");
  const isSeminarPricing = isSeminarFormEvent({
    eventType: { name: eventTypeName },
    orderTemplateId,
    eventFees
  });

  return (
    <fieldset disabled={disabled} className="flex flex-col gap-6 mb-6">
      <p className="text-title-large mb-2"> How much should be charged for this event?</p>
      {isSeminarPricing ? (
        <SeminarPricingFields hasRegistrations={hasRegistrations} disabled={disabled} />
      ) : (
        <LegacyPricingFields hasRegistrations={hasRegistrations} />
      )}
    </fieldset>
  );
};

export default PricingStep;
